import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Box,
  Typography,
  Divider,
} from "@mui/material";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import FolderRoundedIcon from "@mui/icons-material/FolderRounded";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import WebRoundedIcon from "@mui/icons-material/WebRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import PeopleRoundedIcon from "@mui/icons-material/PeopleRounded";
import BadgeRoundedIcon from "@mui/icons-material/BadgeRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import DesktopWindowsRoundedIcon from "@mui/icons-material/DesktopWindowsRounded";
import ContactsRoundedIcon from "@mui/icons-material/ContactsRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import Logo from "../../../../../assets/app-logo.svg";

const S = {
  bg: "#0d1117",
  border: "1px solid rgba(255,255,255,0.06)",
  activeBg: "rgba(124, 58, 237, 0.12)",
  activeBorder: "#7c3aed",
  activeText: "#ffffff",
  activeIcon: "#a78bfa",
  hoverBg: "rgba(255,255,255,0.05)",
  text: "#9ca3af",
  textSecondary: "#6b7280",
  section: "#4b5563",
};

const ATTRIBUTE_PATHS = [
  "/admin/religions", "/admin/caste", "/admin/sub-caste", "/admin/countries",
  "/admin/states", "/admin/cities", "/admin/on-behalf", "/admin/marital-status",
  "/admin/languages", "/admin/professions", "/admin/qualifications",
  "/admin/community-values", "/admin/star", "/admin/raasi", "/admin/lagnam", "/admin/paadham",
];

const MEMBER_PATHS = [
  "/admin/all-members", "/admin/free-members", "/admin/premium-members", "/admin/expired-members",
  "/admin/deleted-members", "/admin/reported-members", "/admin/scheduler-members",
];

const STAFF_PATHS = ["/admin/staffs", "/admin/roles", "/admin/permission"];
const WALLET_PATHS = ["/admin/wallet-transactions", "/admin/recharge-request"];
const WEBSITE_PATHS = ["/admin/website-setup", "/admin/appearances"];

function Sidebar() {
  const location = useLocation();

  const matchesAny = (paths: string[]) =>
    paths.some((p) => location.pathname.startsWith(p));

  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({
    website: matchesAny(WEBSITE_PATHS),
    attributes: matchesAny(ATTRIBUTE_PATHS),
    members: matchesAny(MEMBER_PATHS),
    staffs: matchesAny(STAFF_PATHS),
    wallet: matchesAny(WALLET_PATHS),
  });

  const handleClose = () => {
    const sidePanel = document.getElementById("app-sidepanel");
    if (sidePanel && sidePanel.classList.contains("sidepanel-visible")) {
      sidePanel.classList.remove("sidepanel-visible");
      sidePanel.classList.add("sidepanel-hidden");
    }
  };

  const toggleMenu = (key: string) => {
    setOpenMenus((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const isActive = (path: string, exact = false) =>
    exact ? location.pathname === path : location.pathname.startsWith(path);

  const navItemSx = (active: boolean) => ({
    borderRadius: "8px",
    mx: 1,
    mb: 0.25,
    py: "5px",
    pl: "8px",
    minHeight: 34,
    borderLeft: `3px solid ${active ? S.activeBorder : "transparent"}`,
    bgcolor: active ? S.activeBg : "transparent",
    "&:hover": { bgcolor: active ? S.activeBg : S.hoverBg },
    transition: "all 0.15s ease",
  });

  const subItemSx = (active: boolean) => ({
    borderRadius: "6px",
    mx: 1,
    mb: 0.15,
    py: "3px",
    pl: "30px",
    minHeight: 28,
    borderLeft: `2px solid ${active ? S.activeBorder : "transparent"}`,
    bgcolor: active ? S.activeBg : "transparent",
    "&:hover": { bgcolor: active ? S.activeBg : S.hoverBg },
    transition: "all 0.15s ease",
  });

  const navIconColor = (active: boolean) => ({
    minWidth: 30,
    color: active ? S.activeIcon : S.text,
  });

  const navTextProps = (active: boolean) => ({
    fontSize: "0.8125rem",
    fontWeight: active ? 600 : 400,
    color: active ? S.activeText : S.text,
    lineHeight: 1.3,
  });

  const subTextProps = (active: boolean) => ({
    fontSize: "0.75rem",
    fontWeight: active ? 500 : 400,
    color: active ? S.activeText : S.text,
    lineHeight: 1.3,
  });

  const SectionLabel = ({ children }: { children: string }) => (
    <Typography
      variant="caption"
      sx={{
        px: 2,
        pt: 1,
        pb: 0.25,
        display: "block",
        color: S.section,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: 1.1,
        fontSize: "0.6rem",
      }}
    >
      {children}
    </Typography>
  );

  const renderSubItems = (
    items: { label: string; to: string }[]
  ) =>
    items.map((item) => {
      const active = isActive(item.to);
      return (
        <ListItemButton
          key={item.label}
          component={NavLink as any}
          to={item.to}
          sx={subItemSx(active)}
        >
          <ListItemIcon sx={{ minWidth: 22 }}>
            <FiberManualRecordIcon
              sx={{ fontSize: 5, color: active ? S.activeIcon : S.textSecondary }}
            />
          </ListItemIcon>
          <ListItemText
            primary={item.label}
            primaryTypographyProps={subTextProps(active)}
          />
        </ListItemButton>
      );
    });
  return (
    <div id="app-sidepanel" className="app-sidepanel">
      <div id="sidepanel-drop" className="sidepanel-drop" />
      <div
        className="sidepanel-inner d-flex flex-column"
        style={{ background: S.bg, height: "100%" }}
      >
        <button
          onClick={handleClose}
          id="sidepanel-close"
          className="sidepanel-close d-xl-none"
          style={{ position: "absolute", top: 14, right: 14, background: "rgba(255,255,255,0.06)", border: "none", borderRadius: "8px", width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: S.text, zIndex: 10 }}
        >
          <CloseRoundedIcon fontSize="small" />
        </button>

        <Box sx={{ px: 2, py: 1.5, borderBottom: S.border, background: "rgba(124,58,237,0.04)" }}>
          <NavLink to="/admin" style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: 10 }}>
            <Box sx={{ width: 32, height: 32, borderRadius: "8px", background: "linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 3px 10px rgba(124,58,237,0.4)", flexShrink: 0 }}>
              <img src={Logo} alt="logo" style={{ width: 18, height: 18, filter: "brightness(0) invert(1)" }} />
            </Box>
            <Box>
              <Typography sx={{ color: "#ffffff", fontWeight: 700, fontSize: "0.9rem", lineHeight: 1.2 }}>Matrimony</Typography>
              <Typography sx={{ color: "#7c3aed", fontWeight: 600, fontSize: "0.58rem", textTransform: "uppercase", letterSpacing: 0.8 }}>Admin Panel</Typography>
            </Box>
          </NavLink>
        </Box>

        <Box sx={{ flex: 1, overflowY: "auto", py: 0.5, "&::-webkit-scrollbar": { width: 4 }, "&::-webkit-scrollbar-track": { background: "transparent" }, "&::-webkit-scrollbar-thumb": { background: "rgba(255,255,255,0.1)", borderRadius: 2 } }}>
          <SectionLabel>Overview</SectionLabel>
          <ListItemButton component={NavLink as any} to="/admin" end sx={navItemSx(isActive("/admin", true))}>
            <ListItemIcon sx={navIconColor(isActive("/admin", true))}><DashboardRoundedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Dashboard" primaryTypographyProps={navTextProps(isActive("/admin", true))} />
          </ListItemButton>
          <ListItemButton component={NavLink as any} to="/admin/contact-list" sx={navItemSx(isActive("/admin/contact-list"))}>
            <ListItemIcon sx={navIconColor(isActive("/admin/contact-list"))}><ContactsRoundedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Contact List" primaryTypographyProps={navTextProps(isActive("/admin/contact-list"))} />
          </ListItemButton>
          <ListItemButton component={NavLink as any} to="/admin/packages" sx={navItemSx(isActive("/admin/packages"))}>
            <ListItemIcon sx={navIconColor(isActive("/admin/packages"))}><FolderRoundedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Premium Packages" primaryTypographyProps={navTextProps(isActive("/admin/packages"))} />
          </ListItemButton>
          <ListItemButton component={NavLink as any} to="/admin/db-backups" sx={navItemSx(isActive("/admin/db-backups"))}>
            <ListItemIcon sx={navIconColor(isActive("/admin/db-backups"))}><FolderRoundedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="DB Backups" primaryTypographyProps={navTextProps(isActive("/admin/db-backups"))} />
          </ListItemButton>
          <ListItemButton component={NavLink as any} to="/admin/happy-stories" sx={navItemSx(isActive("/admin/happy-stories"))}>
            <ListItemIcon sx={navIconColor(isActive("/admin/happy-stories"))}><FavoriteRoundedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Happy Stories" primaryTypographyProps={navTextProps(isActive("/admin/happy-stories"))} />
          </ListItemButton>

          <Divider sx={{ my: 0.75, mx: 1.5, borderColor: "rgba(255,255,255,0.05)" }} />
          <SectionLabel>Configuration</SectionLabel>
          <ListItemButton onClick={() => toggleMenu("website")} sx={navItemSx(openMenus.website)}>
            <ListItemIcon sx={navIconColor(openMenus.website)}><WebRoundedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Website Setup" primaryTypographyProps={navTextProps(openMenus.website)} />
            {openMenus.website ? <ExpandLessRoundedIcon sx={{ color: S.textSecondary, fontSize: 15 }} /> : <ExpandMoreRoundedIcon sx={{ color: S.textSecondary, fontSize: 15 }} />}
          </ListItemButton>
          <Collapse in={openMenus.website} timeout="auto" unmountOnExit>
            <List disablePadding>{renderSubItems([{ label: "Header", to: "/admin/website-setup" }, { label: "Appearance", to: "/admin/appearances" }])}</List>
          </Collapse>
          <ListItemButton onClick={() => toggleMenu("attributes")} sx={navItemSx(openMenus.attributes)}>
            <ListItemIcon sx={navIconColor(openMenus.attributes)}><TuneRoundedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Profile Attributes" primaryTypographyProps={navTextProps(openMenus.attributes)} />
            {openMenus.attributes ? <ExpandLessRoundedIcon sx={{ color: S.textSecondary, fontSize: 15 }} /> : <ExpandMoreRoundedIcon sx={{ color: S.textSecondary, fontSize: 15 }} />}
          </ListItemButton>
          <Collapse in={openMenus.attributes} timeout="auto" unmountOnExit>
            <List disablePadding>{renderSubItems([{ label: "Religions", to: "/admin/religions" }, { label: "Caste", to: "/admin/caste" }, { label: "Sub-Caste", to: "/admin/sub-caste" }, { label: "Country", to: "/admin/countries" }, { label: "State", to: "/admin/states" }, { label: "City", to: "/admin/cities" }, { label: "On Behalf", to: "/admin/on-behalf" }, { label: "Marital Status", to: "/admin/marital-status" }, { label: "Language", to: "/admin/languages" }, { label: "Profession", to: "/admin/professions" }, { label: "Qualification", to: "/admin/qualifications" }, { label: "Community Values", to: "/admin/community-values" }, { label: "Star", to: "/admin/star" }, { label: "Raasi", to: "/admin/raasi" }, { label: "Lagnam", to: "/admin/lagnam" }, { label: "Paadham", to: "/admin/paadham" }])}</List>
          </Collapse>

          <Divider sx={{ my: 0.75, mx: 1.5, borderColor: "rgba(255,255,255,0.05)" }} />
          <SectionLabel>Users</SectionLabel>
          <ListItemButton onClick={() => toggleMenu("members")} sx={navItemSx(openMenus.members)}>
            <ListItemIcon sx={navIconColor(openMenus.members)}><PeopleRoundedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Members" primaryTypographyProps={navTextProps(openMenus.members)} />
            {openMenus.members ? <ExpandLessRoundedIcon sx={{ color: S.textSecondary, fontSize: 15 }} /> : <ExpandMoreRoundedIcon sx={{ color: S.textSecondary, fontSize: 15 }} />}
          </ListItemButton>
          <Collapse in={openMenus.members} timeout="auto" unmountOnExit>
            <List disablePadding>{renderSubItems([{ label: "All Members", to: "/admin/all-members" }, { label: "Scheduler", to: "/admin/scheduler-members" }, { label: "Free Members", to: "/admin/free-members" }, { label: "Premium Members", to: "/admin/premium-members" }, { label: "Expired Members", to: "/admin/expired-members" }, { label: "Deleted Members", to: "/admin/deleted-members" }, { label: "Reported Members", to: "/admin/reported-members" }])}</List>
          </Collapse>
          <ListItemButton onClick={() => toggleMenu("staffs")} sx={navItemSx(openMenus.staffs)}>
            <ListItemIcon sx={navIconColor(openMenus.staffs)}><BadgeRoundedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Staffs" primaryTypographyProps={navTextProps(openMenus.staffs)} />
            {openMenus.staffs ? <ExpandLessRoundedIcon sx={{ color: S.textSecondary, fontSize: 15 }} /> : <ExpandMoreRoundedIcon sx={{ color: S.textSecondary, fontSize: 15 }} />}
          </ListItemButton>
          <Collapse in={openMenus.staffs} timeout="auto" unmountOnExit>
            <List disablePadding>{renderSubItems([{ label: "All Staffs", to: "/admin/staffs" }, { label: "Roles", to: "/admin/roles" }, { label: "Permissions", to: "/admin/permission" }])}</List>
          </Collapse>

          <Divider sx={{ my: 0.75, mx: 1.5, borderColor: "rgba(255,255,255,0.05)" }} />
          <SectionLabel>Finance</SectionLabel>
          <ListItemButton onClick={() => toggleMenu("wallet")} sx={navItemSx(openMenus.wallet)}>
            <ListItemIcon sx={navIconColor(openMenus.wallet)}><AccountBalanceWalletRoundedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Wallet" primaryTypographyProps={navTextProps(openMenus.wallet)} />
            {openMenus.wallet ? <ExpandLessRoundedIcon sx={{ color: S.textSecondary, fontSize: 15 }} /> : <ExpandMoreRoundedIcon sx={{ color: S.textSecondary, fontSize: 15 }} />}
          </ListItemButton>
          <Collapse in={openMenus.wallet} timeout="auto" unmountOnExit>
            <List disablePadding>{renderSubItems([{ label: "Transaction History", to: "/admin/wallet-transactions" }, { label: "Recharge Requests", to: "/admin/recharge-request" }])}</List>
          </Collapse>
        </Box>

        <Box sx={{ borderTop: S.border, pt: 0.25, pb: 0.5 }}>
          <ListItemButton component={NavLink as any} to="/admin/system" sx={navItemSx(isActive("/admin/system"))}>
            <ListItemIcon sx={navIconColor(isActive("/admin/system"))}><DesktopWindowsRoundedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="System" primaryTypographyProps={navTextProps(isActive("/admin/system"))} />
          </ListItemButton>
        </Box>
      </div>
    </div>
  );
}
export default Sidebar;
